import { useState } from "react";
import Header from "../../components/feature/Header";
import Footer from "../../components/feature/Footer";
import { Link } from "react-router-dom";

const FAQS = [
  {
    category: "Tree Removal",
    items: [
      { q: "Who removes trees near me in Northern Virginia?", a: "Diversified Tree Services LLC provides professional tree removal across Northern Virginia including Fredericksburg, Stafford County, Spotsylvania, King George, Culpeper, Manassas, Woodbridge, and more. Call (540) 776-4488 for a free estimate today." },
      { q: "How much does tree removal cost in Fredericksburg, VA?", a: "Tree removal costs in Northern Virginia typically range from $350 for small trees to $2,500+ for large, complex removals. Factors include tree height, trunk diameter, location, proximity to structures, and access. We offer free on-site written estimates with no obligation." },
      { q: "How long does tree removal take?", a: "Most residential tree removals are completed in 2–6 hours. Small, accessible trees may take under 2 hours, while large trees near structures requiring precise rigging can take 6–8 hours or a full day. Full cleanup is always included." },
      { q: "When should I remove a tree vs. trim it?", a: "Remove a tree when it's dead, diseased beyond recovery, structurally compromised, or poses a direct hazard to your home or power lines. Trim trees when they're overgrown, have deadwood, or branches are encroaching on structures. Our arborists provide free assessments." },
      { q: "Do you offer emergency tree removal?", a: "Yes. We provide 24/7 emergency tree removal for storm-damaged or fallen trees. Call (540) 776-4488 immediately for emergency response. We prioritize dangerous situations and dispatch as quickly as possible." },
    ],
  },
  {
    category: "Tree Trimming",
    items: [
      { q: "When is the best time to trim trees?", a: "Late winter to early spring is ideal for most trees as they're dormant and disease risk is low. However, dead or dangerous branches should be removed immediately year-round. Clearance trimming can be done any time. Our arborists can advise on timing for your specific tree species." },
      { q: "How often should trees be trimmed?", a: "Most mature trees benefit from professional trimming every 2–3 years. Fast-growing trees and young trees may need annual attention. Fruit trees often require yearly pruning for best production. A one-time arborist assessment can establish a trimming schedule for your property." },
      { q: "Can trimming damage my tree?", a: "Improper trimming (tree topping, over-pruning) can severely damage trees. Our ISA Certified Arborists follow ANSI A300 pruning standards to ensure every cut promotes healing and long-term tree health. We never 'top' trees." },
    ],
  },
  {
    category: "Stump Grinding",
    items: [
      { q: "What is stump grinding?", a: "Stump grinding uses a specialized machine with a rotating cutting disc to chip the stump and roots into wood chips, grinding them 12 or more inches below the soil surface. This eliminates the stump visually and structurally, allowing you to replant or build over the area." },
      { q: "How much does stump grinding cost?", a: "Stump grinding in Northern Virginia starts at $150 for small stumps and goes up based on diameter. Most stumps range from $150–$450. We offer discounts for multiple stumps. Call for a free estimate based on your specific stumps." },
      { q: "What happens to the wood chips after grinding?", a: "The resulting wood chips can be spread as mulch in your garden beds or hauled away at no extra charge — your choice. Many customers love using the natural mulch for their landscaping." },
    ],
  },
  {
    category: "Pricing & Scheduling",
    items: [
      { q: "Do you offer free estimates?", a: "Yes, we provide completely free, no-obligation on-site estimates. One of our arborists will come out, assess your tree service needs, and provide a written estimate. There's no pressure and no charge for the estimate." },
      { q: "Are you licensed and insured?", a: "Absolutely. Diversified Tree Services LLC is fully licensed and carries comprehensive general liability insurance and workers' compensation. We can provide proof of insurance before any work begins for your peace of mind." },
      { q: "How quickly can you schedule service?", a: "For standard tree work, we can typically schedule within 1–5 business days. For emergency situations involving fallen trees or immediate hazards, we provide same-day or next-day response. Call (540) 776-4488 for availability." },
    ],
  },
];

export default function FAQPage() {
  const [open, setOpen] = useState<string | null>(null);

  return (
    <>
      <Header />
      <main>
        <section className="py-20 bg-[#1a3a2e] text-center">
          <div className="max-w-4xl mx-auto px-4">
            <span className="text-[#e8632a] font-bold text-sm uppercase tracking-widest block mb-3">FAQ</span>
            <h1 className="text-5xl font-black text-white mb-4">Frequently Asked Questions</h1>
            <p className="text-white/70 text-xl">
              Everything homeowners ask about tree removal, trimming, and stump grinding in Northern Virginia.
            </p>
          </div>
        </section>

        {/* AI answer block */}
        <section className="py-10 bg-white border-b border-gray-100">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-[#f8f6f3] rounded-2xl p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#e8632a] rounded-xl flex-shrink-0">
                  <i className="ri-lightbulb-fill text-white text-xl"></i>
                </div>
                <div>
                  <h2 className="font-black text-[#1e1e1e] text-xl mb-3">Understanding Tree Services: A Quick Guide</h2>
                  <p className="text-gray-600 leading-relaxed mb-3">
                    <strong>Tree removal</strong> involves safely cutting down a tree, sectioning it, and hauling away all debris. It's recommended when a tree is dead, diseased, structurally compromised, or poses a safety hazard to your home or utility lines.
                  </p>
                  <p className="text-gray-600 leading-relaxed mb-3">
                    <strong>Tree trimming</strong> is the strategic removal of branches to improve tree health, shape the canopy, and remove hazards. Properly trimmed trees live longer, look better, and are safer in storms.
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    <strong>Stump grinding</strong> eliminates tree stumps left after removal by grinding them below soil level. This prevents regrowth, eliminates tripping hazards, and restores usable yard space.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            {FAQS.map((cat) => (
              <div key={cat.category} className="mb-12">
                <h2 className="text-2xl font-black text-[#1e1e1e] mb-6 flex items-center gap-3">
                  <span className="w-8 h-1 bg-[#e8632a] rounded-full inline-block"></span>
                  {cat.category}
                </h2>
                <div className="space-y-3">
                  {cat.items.map((faq, i) => {
                    const key = `${cat.category}-${i}`;
                    return (
                      <div key={key} className="border border-gray-200 rounded-xl overflow-hidden">
                        <button
                          className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => setOpen(open === key ? null : key)}
                        >
                          <span className="font-bold text-[#1e1e1e] pr-4">{faq.q}</span>
                          <div className={`w-7 h-7 flex items-center justify-center rounded-full border-2 flex-shrink-0 transition-all ${open === key ? "bg-[#e8632a] border-[#e8632a] text-white" : "border-gray-300 text-gray-400"}`}>
                            <i className={`text-sm ${open === key ? "ri-subtract-line" : "ri-add-line"}`}></i>
                          </div>
                        </button>
                        {open === key && (
                          <div className="px-6 pb-5 bg-gray-50">
                            <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="bg-[#1a3a2e] rounded-2xl p-8 text-center">
              <h3 className="text-white font-black text-2xl mb-3">Still Have Questions?</h3>
              <p className="text-white/70 mb-6">Our team is happy to answer any questions about your specific tree situation.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:+15407764488" className="flex items-center justify-center gap-2 px-6 py-3 bg-[#e8632a] text-white font-bold rounded-lg uppercase tracking-wide hover:bg-[#d0541e] cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-fill"></i>(540) 776-4488
                </a>
                <Link to="/contact" className="px-6 py-3 border-2 border-white text-white font-bold rounded-lg uppercase tracking-wide hover:bg-white hover:text-[#1a3a2e] transition-all cursor-pointer whitespace-nowrap">
                  Send Us a Message
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
